import React from "react";
import { CheckCircle2, AlertTriangle, XCircle, Clock, CircleDot, Circle, Ban, CheckCheck, Info } from "lucide-react";

const TONES = {
  success: "bg-cp-success-soft text-cp-success border-cp-success-border",
  warning: "bg-cp-warning-soft text-cp-warning border-cp-warning-border",
  danger: "bg-cp-danger-soft text-cp-danger border-cp-danger-border",
  info: "bg-cp-info-soft text-cp-info border-cp-info-border",
  neutral: "bg-cp-neutral-soft text-cp-text-2 border-cp-neutral-border",
  accent: "bg-cp-accent-soft text-cp-accent border-cp-accent-border",
  dark: "bg-cp-navy text-white border-cp-navy",
  amber: "bg-cp-amber text-white border-cp-amber",
};

/** Statusuri proiect (client_status) */
export const PROJECT_STATUS = {
  "On Track": { label: "În grafic", tone: "success", icon: CheckCircle2 },
  "Attention Required": { label: "Necesită atenție", tone: "warning", icon: AlertTriangle },
  "Delayed": { label: "Întârziat", tone: "danger", icon: XCircle },
  "Completed": { label: "Finalizat", tone: "dark", icon: CheckCheck },
};
/** Health index */
export const HEALTH_LEVEL = {
  "On Track": { label: "În grafic", tone: "success", icon: CheckCircle2 },
  "Attention Required": { label: "Atenție", tone: "warning", icon: AlertTriangle },
  "Critical": { label: "Critic", tone: "danger", icon: XCircle },
};
/** Etape timeline */
export const STAGE_STATUS = {
  "Upcoming": { label: "Urmează", tone: "neutral", icon: Circle },
  "In Progress": { label: "În curs", tone: "accent", icon: CircleDot },
  "Completed": { label: "Finalizat", tone: "success", icon: CheckCircle2 },
  "Delayed": { label: "Întârziat", tone: "danger", icon: AlertTriangle },
};
/** Decizii */
export const DECISION_STATUS = {
  pending: { label: "În așteptare", tone: "warning", icon: Clock },
  approved: { label: "Aprobat", tone: "success", icon: CheckCircle2 },
  rejected: { label: "Respins", tone: "danger", icon: Ban },
  acknowledged: { label: "Confirmat", tone: "info", icon: Info },
};
export const DECISION_KIND = {
  approval: "Solicitare de aprobare", decision: "Decizie", confirmation: "Confirmare", selection: "Selectare variantă",
  info: "Informare", validation: "Validare document", change_request: "Solicitare de modificare",
};
/** Riscuri */
export const RISK_LEVEL = { low: { label: "Scăzut", tone: "success" }, medium: { label: "Mediu", tone: "warning" }, high: { label: "Ridicat", tone: "danger" } };
export const RISK_STATUS = {
  open: { label: "Deschis", tone: "danger", icon: AlertTriangle }, monitoring: { label: "Monitorizat", tone: "warning", icon: CircleDot },
  mitigated: { label: "Atenuat", tone: "info", icon: CheckCircle2 }, closed: { label: "Închis", tone: "neutral", icon: CheckCheck },
};
/** Calitate */
export const QUALITY_KIND = { inspection: "Inspecție", test: "Test", nonconformity: "Neconformitate", audit: "Audit", acceptance: "Recepție" };
export const QUALITY_RESULT = {
  passed: { label: "Conform", tone: "success", icon: CheckCircle2 }, open: { label: "În lucru", tone: "warning", icon: Clock },
  failed: { label: "Neconform", tone: "danger", icon: XCircle }, closed: { label: "Închis", tone: "neutral", icon: CheckCheck },
};
/** Costuri */
export const COST_STATUS = {
  planned: { label: "Planificat", tone: "neutral" }, approved: { label: "Aprobat", tone: "accent" },
  invoiced: { label: "Facturat", tone: "warning" }, paid: { label: "Plătit", tone: "success" },
};

export function Badge({ tone = "neutral", icon: Icon, children, size = "md", className = "", dot = false, ...rest }) {
  const sz = size === "sm" ? "h-[22px] px-2 text-[11px] gap-1 [&_svg]:h-3 [&_svg]:w-3" : "h-6 px-2.5 text-[12px] gap-1.5 [&_svg]:h-3.5 [&_svg]:w-3.5";
  return (
    <span className={`inline-flex items-center whitespace-nowrap font-semibold rounded-full border ${TONES[tone]} ${sz} ${className}`} {...rest}>
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />}
      {Icon && <Icon aria-hidden="true" />}
      {children}
    </span>
  );
}

export function StatusBadge({ map, value, fallback = "On Track", size, className, noIcon = false, ...rest }) {
  const s = map[value] || map[fallback] || { label: value, tone: "neutral" };
  return <Badge tone={s.tone} icon={noIcon ? undefined : s.icon} size={size} className={className} {...rest}>{s.label}</Badge>;
}

const DOT = { success: "is-success", warning: "is-warning", danger: "is-danger", info: "is-accent", neutral: "is-neutral", accent: "is-accent", dark: "is-neutral" };
const TXT = { success: "text-cp-success", warning: "text-cp-warning", danger: "text-cp-danger", info: "text-cp-info", neutral: "text-cp-text-2", accent: "text-cp-accent", dark: "text-cp-text" };

/** Punct de status + text colorat (ca în machetă: „● În grafic”) */
export function StatusDot({ map, value, fallback = "On Track", className = "", bold = true }) {
  const s = map[value] || map[fallback] || { label: value, tone: "neutral" };
  return (
    <span className={`inline-flex items-center gap-2 text-[13px] ${bold ? "font-semibold" : "font-medium"} ${TXT[s.tone]} ${className}`}>
      <span className={`cp-dot ${DOT[s.tone]}`} aria-hidden="true" />{s.label}
    </span>
  );
}
